// src/hooks/useTeamAccess.js
//
// Resolves the signed-in user's effective ACCESS level on a specific team,
// delegating to src/constants/roles.js::resolveTeamAccess. Mirrors the
// server-side resolver in functions/core/roles.js so client & server
// always agree on "can this user do X here?".
//
// Returns: { access, loading, refresh }
//   • access: one of ACCESS.* from roles.js ('none' / 'team_staff' /
//     'team_owner' / 'club_admin' / 'club_director' / 'platform_support' /
//     'platform_founder'). Defaults to ACCESS.NONE while loading.
//   • loading: true until the first resolve completes.
//   • refresh(): re-runs the resolver. Call after a local mutation
//     (role change, invite accepted) to pick up the new state without
//     waiting for a page reload.
//
// Usage:
//   const { access, loading } = useTeamAccess(teamId);
//   if (!loading && !canInviteStaff(access)) return <NotAllowed />;
//
// Why not a live Firestore listener: resolveTeamAccess reads up to three
// docs (teams/{id}, members/{uid}, clubs/{clubId}/members/{uid}). Opening
// listeners for all three per consuming component would multiply our
// read quota. Most screens that care about access are either one-shot
// (route guards) or have their own listeners on the same docs they can
// use to trigger refresh(). If a component truly needs live-updating
// access, it can call refresh() from its listener callbacks.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { ACCESS, resolveTeamAccess } from '@/constants/roles';

export function useTeamAccess(teamId, { clubId = null } = {}) {
  const { firebaseUser, claims } = useAuth();
  const [access, setAccess]   = useState(ACCESS.NONE);
  const [loading, setLoading] = useState(true);
  // bumpKey triggers a re-resolve without changing any input value.
  const [bumpKey, setBumpKey] = useState(0);

  // Track the latest call so out-of-order resolves (e.g. teamId changed
  // mid-flight) don't clobber a newer result with a stale one.
  const latestCallRef = useRef(0);

  const refresh = useCallback(() => setBumpKey((n) => n + 1), []);

  useEffect(() => {
    const myCall = ++latestCallRef.current;

    // No user yet (signed out, still loading auth) → NONE.
    if (!firebaseUser?.uid) {
      setAccess(ACCESS.NONE);
      setLoading(false);
      return;
    }
    // No teamId (route param missing) → NONE, not an error.
    if (!teamId) {
      setAccess(ACCESS.NONE);
      setLoading(false);
      return;
    }

    setLoading(true);
    resolveTeamAccess({
      uid:    firebaseUser.uid,
      teamId,
      clubId,
      claims: claims || {},
    })
      .then((resolved) => {
        if (latestCallRef.current !== myCall) return; // stale
        setAccess(resolved);
      })
      .catch((err) => {
        if (latestCallRef.current !== myCall) return;
        // Fail closed — if the resolver throws (rare; Firestore errors
        // are already swallowed inside it), treat the user as having no
        // access rather than falsely granting.
        console.warn('[useTeamAccess] resolve failed', err?.message);
        setAccess(ACCESS.NONE);
      })
      .finally(() => {
        if (latestCallRef.current !== myCall) return;
        setLoading(false);
      });
  }, [firebaseUser?.uid, teamId, clubId, claims, bumpKey]);

  return { access, loading, refresh };
}

export default useTeamAccess;
